import type { InventoryItemType } from '../types/venue';

/**
 * REPORTING-CENTER.2.1 — shared inventory-item display helpers.
 *
 * Item rows across the reports (Transaction Register, Stock Card picker,
 * Movements report) show a bottle/size caption under the item name. But
 * InventoryItem.displayName is the canonical string and for WINE items it
 * already ends with the size (e.g. "Château Margaux 2020 750 ml"), so a naive
 * caption repeats it. These helpers centralize the size string and the
 * "don't repeat what the name already says" rule.
 */

/** Human size string, or null when size is not fully specified. */
export const sizeLabel = (
  sizeValue?: number | null,
  sizeUnit?: string | null,
): string | null => {
  if (sizeValue == null || !sizeUnit) return null;
  return `${sizeValue} ${sizeUnit}`;
};

// Whitespace-insensitive, case-insensitive tail comparison so "750ml",
// "750 ml", and "750  ML" all count as already-present.
const squash = (s: string): string => s.toLowerCase().replace(/\s+/g, '');

/**
 * The size caption to render under a name — or null when it would be redundant
 * because the displayName already ends with that size.
 */
export const sizeCaption = (
  displayName: string | null | undefined,
  sizeValue?: number | null,
  sizeUnit?: string | null,
): string | null => {
  const size = sizeLabel(sizeValue, sizeUnit);
  if (!size) return null;
  const name = displayName ?? '';
  return squash(name).endsWith(squash(size)) ? null : size;
};

// ── RECIPE-TYPE.R6 — item-type presentation ──────────────────────────────────
/**
 * The single source of truth for how a stored InventoryItem `type` is presented.
 *
 * Label AND chip colour live together here so a screen can never show the right
 * word in the wrong colour, and so Phase C's BASIC is added by appending
 * ONE entry rather than editing a ternary in every component. The values are the
 * ones already in use across the inventory screens — nothing is redesigned.
 *
 * `color: null` means "use this surface's neutral/secondary text colour". Each
 * screen keeps its own neutral expression (the list resolves
 * theme.palette.text.secondary, the detail page uses the 'text.secondary' token),
 * so only the TYPED colours are centralised.
 */
export interface ItemTypePresentation {
  label: string;
  color: string | null;
}

export const ITEM_TYPE_PRESENTATION: Record<string, ItemTypePresentation> = {
  WINE:         { label: 'Wine',         color: '#6941c6' },
  PURCHASED:    { label: 'Purchased',    color: null },
  MANUFACTURED: { label: 'Manufactured', color: '#b45309' },
  // Matches the colour the existing "Made from a recipe" chip already uses.
  RECIPE:       { label: 'Recipe',       color: '#9c27b0' },
  // BASIC-ITEM.C6 — teal-700. The three existing typed colours occupy the
  // purple (#6941c6), magenta (#9c27b0) and amber (#b45309) ranges, so teal is
  // the nearest hue that stays clearly distinct from all of them — in particular
  // from RECIPE, which a basic item must never be confused with. Same
  // saturation/darkness band as the amber (both are 700-weight tokens), so the
  // chips read as one family at a glance.
  BASIC: { label: 'Basic',     color: '#0f766e' },
};

/**
 * Title-case an item type for display (WINE → Wine, RECIPE → Recipe).
 *
 * Known types come from ITEM_TYPE_PRESENTATION so RECIPE can never fall through
 * to "Purchased" — the bug class this replaces. Unrecognised values keep the
 * original generic title-case fallback, and null/undefined still return null.
 */
export const humanizeItemType = (type?: string | null): string | null => {
  if (!type) return null;
  const known = ITEM_TYPE_PRESENTATION[type];
  if (known) return known.label;
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

/** Chip foreground for an item type; null → the caller's neutral colour. */
export const itemTypeColor = (type?: string | null): string | null =>
  (type ? ITEM_TYPE_PRESENTATION[type]?.color ?? null : null);

/**
 * RECIPE-TYPE.R7 — the stored types offered as an item-type FILTER, in display
 * order. Every filter surface (item list, dashboard, alerts, transaction
 * register) maps over this with humanizeItemType, so there is exactly one list
 * and one label source instead of four hard-coded <MenuItem> blocks. Phase C
 * appends one entry here.
 *
 * Order preserves the pre-R7 option order; RECIPE is appended.
 *
 * NOTE this is a filter list, not a creation list — RECIPE and BASIC are
 * deliberately NOT creatable from the standard inventory-item form (the
 * menu/recipe linking flow creates one; the internal POS basic-item endpoint
 * creates the other).
 */
export const ITEM_TYPE_FILTER_OPTIONS: readonly InventoryItemType[] = [
  'WINE',
  'PURCHASED',
  'MANUFACTURED',
  'RECIPE',
  // BASIC-ITEM.C6 — appended, as R7 anticipated. Every filter surface maps
  // over this list, so the Basic option appears in the item list, dashboard,
  // alerts and transaction register with no per-screen edit.
  'BASIC',
];
